import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Loader2, ClipboardList, Pencil, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { ProdHeader, FabricaGate, StatusBadge, useFabrica, useRoles, fmt, today, addDays, weekStart, DIAS, fmtData, errMsg, ExportBar } from "./shared";

const PRIOS = ["URGENTE", "ALTA", "NORMAL", "BAIXA"];
const PRIO_CLS = { URGENTE: "border-l-red-500", ALTA: "border-l-amber-500", NORMAL: "border-l-sky-500", BAIXA: "border-l-zinc-500" };

function OrdemDialog({ form, setForm, produtos, onSave, saving }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const prod = produtos.find((p) => p.produto_id === form?.produto_id);
  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display uppercase">{form?.id ? `Alterar ${form.numero}` : "Nova ordem de produção"}</DialogTitle>
          <DialogDescription>A ordem fica vinculada à versão aprovada da ficha técnica. Alterações recalculam os materiais.</DialogDescription></DialogHeader>
        {form && (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Produto</Label>
              <Select value={form.produto_id} disabled={!!form.id} onValueChange={(v) => set("produto_id", v)}><SelectTrigger data-testid="op-produto"><SelectValue placeholder="Produto com ficha aprovada" /></SelectTrigger>
                <SelectContent>{produtos.map((p) => <SelectItem key={p.produto_id} value={p.produto_id}>{p.produto_nome} (ficha v{p.versao})</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Quantidade ({prod?.unidade || "un"})</Label><Input type="number" value={form.quantidade} onChange={(e) => set("quantidade", Number(e.target.value))} data-testid="op-quantidade" /></div>
            <div><Label>Data</Label><Input type="date" value={form.data} onChange={(e) => set("data", e.target.value)} data-testid="op-data" /></div>
            {prod && <div className="col-span-2 text-xs text-muted-foreground">Receita rende {fmt(prod.rendimento_qtd)} {prod.unidade} → <b className="text-foreground">{fmt((form.quantidade || 0) / prod.rendimento_qtd)} receitas</b></div>}
            <div><Label>Setor</Label><Input value={form.setor} onChange={(e) => set("setor", e.target.value)} data-testid="op-setor" /></div>
            <div><Label>Responsável</Label><Input value={form.responsavel} onChange={(e) => set("responsavel", e.target.value)} data-testid="op-responsavel" /></div>
            <div><Label>Destino</Label><Input value={form.destino} onChange={(e) => set("destino", e.target.value)} data-testid="op-destino" /></div>
            <div><Label>Prioridade</Label><Select value={form.prioridade} onValueChange={(v) => set("prioridade", v)}><SelectTrigger data-testid="op-prioridade"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIOS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
          <Button onClick={onSave} disabled={saving} data-testid="btn-salvar-op">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiffDialog({ diff, onClose }) {
  return (
    <Dialog open={!!diff} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="diff-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">Necessidades recalculadas</DialogTitle>
          <DialogDescription>Confira a lista de compras da semana para ver necessidades adicionais ou excedentes.</DialogDescription></DialogHeader>
        <div className="space-y-1 text-sm">
          {diff?.diferencas.map((d) => <div key={d.item_id} className="flex justify-between"><span>{d.nome}</span><span className="font-mono">{fmt(d.anterior)} → {fmt(d.nova)} <b className={d.diferenca > 0 ? "text-amber-300" : "text-emerald-300"}>({d.diferenca > 0 ? "+" : ""}{fmt(d.diferenca)})</b></span></div>)}
          {!!diff?.ajustes_requisicoes.length && <div className="pt-2 text-xs text-muted-foreground">Requisições ajustadas: {diff.ajustes_requisicoes.map((a) => `${a.requisicao} ${a.item} ${fmt(a.anterior)}→${fmt(a.nova)}`).join("; ")}</div>}
        </div>
        <DialogFooter><Link to="/producao/compras"><Button variant="outline">Ver lista de compras</Button></Link><Button onClick={onClose}>OK</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OpCard({ op, tec, onEdit, onAction }) {
  const nav = useNavigate();
  const aberta = ["PLANEJADA", "LIBERADA", "EM_PRODUCAO"].includes(op.status);
  return (
    <div className={`rounded-md border border-border border-l-4 ${PRIO_CLS[op.prioridade] || ""} bg-background/60 p-2 space-y-1 cursor-pointer hover:bg-secondary/40 transition-colors`} onClick={() => nav(`/producao/ordens/${op.id}`)} data-testid={`op-card-${op.numero}`}>
      <div className="flex items-center justify-between gap-1"><span className="font-mono text-[11px] text-primary">{op.numero}</span><StatusBadge s={op.status} /></div>
      <div className="text-sm font-semibold leading-tight">{op.produto_nome}</div>
      <div className="text-xs text-muted-foreground">{fmt(op.quantidade)} {op.unidade}{op.setor ? ` · ${op.setor}` : ""}</div>
      {tec && aberta && (
        <div className="flex gap-1 pt-1" onClick={(e) => e.stopPropagation()}>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(op)} data-testid={`op-edit-${op.numero}`}><Pencil className="w-3.5 h-3.5" /></Button>
          {op.status === "PLANEJADA" && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onAction(op, "liberar")} data-testid={`op-liberar-${op.numero}`}><Send className="w-3.5 h-3.5" /></Button>}
          {op.status !== "EM_PRODUCAO" && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => onAction(op, "cancelar")} data-testid={`op-cancelar-${op.numero}`}><XCircle className="w-3.5 h-3.5" /></Button>}
        </div>
      )}
    </div>
  );
}

export default function ProducaoPlanejamento() {
  const fab = useFabrica();
  const { tec } = useRoles();
  const [ini, setIni] = useState(weekStart(today()));
  const [ops, setOps] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [form, setForm] = useState(null);
  const [diff, setDiff] = useState(null);
  const [saving, setSaving] = useState(false);
  const fim = addDays(ini, 6);
  const load = () => fab.unitId && api.get("/producao/ordens", { params: { unit_id: fab.unitId, ini, fim } }).then((r) => setOps(r.data));
  useEffect(() => { load(); }, [fab.unitId, ini]); // eslint-disable-line
  useEffect(() => { api.get("/producao/fichas").then((r) => setProdutos(r.data.filter((f) => f.status === "APROVADA"))); }, []);

  const novo = (data) => setForm({ unit_id: fab.unitId, produto_id: "", quantidade: 100, data, setor: "", responsavel: "", destino: "", prioridade: "NORMAL", observacao: "" });
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, unit_id: fab.unitId };
      if (form.id) { const r = await api.put(`/producao/ordens/${form.id}`, body); if (r.data.diferencas.length) setDiff(r.data); else toast.success("Ordem atualizada"); }
      else { const r = await api.post("/producao/ordens", body); toast.success(`Ordem ${r.data.numero} criada`); }
      setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const action = async (op, a) => {
    let body = {};
    if (a === "cancelar") { const m = window.prompt(`Motivo do cancelamento de ${op.numero}:`); if (!m) return; body = { motivo: m }; }
    try { await api.post(`/producao/ordens/${op.id}/${a}`, body); toast.success(a === "liberar" ? "Ordem liberada" : "Ordem cancelada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const requisitar = async (params) => {
    try { const r = await api.post("/producao/requisicoes", { unit_id: fab.unitId, ...params }); toast.success(`Requisição ${r.data.numero} emitida (materiais reservados, sem consumo)`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [{ key: "data", label: "Data" }, { key: "numero", label: "OP" }, { key: "produto_nome", label: "Produto" }, { key: "quantidade", label: "Qtd", align: "right" },
    { key: "setor", label: "Setor" }, { key: "responsavel", label: "Responsável" }, { key: "destino", label: "Destino" }, { key: "prioridade", label: "Prioridade" }, { key: "status", label: "Status" }];

  return (
    <div className="space-y-5" data-testid="prod-planejamento-page">
      <ProdHeader title="Planejamento" icon={CalendarDays} fab={fab}>
        {tec && fab.unitId && <Button onClick={() => novo(ini)} data-testid="btn-nova-op"><Plus className="w-4 h-4" /> Nova ordem</Button>}
      </ProdHeader>
      <FabricaGate fab={fab}>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={() => setIni(addDays(ini, -7))} data-testid="semana-anterior"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-display font-bold uppercase" data-testid="semana-label">Semana {fmtData(ini)} — {fmtData(fim)}</div>
          <Button variant="outline" size="icon" onClick={() => setIni(addDays(ini, 7))} data-testid="semana-proxima"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setIni(weekStart(today()))}>Hoje</Button>
          <div className="ml-auto flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => requisitar({ semana_ini: ini })} data-testid="btn-req-semana"><ClipboardList className="w-4 h-4" /> Requisição da semana</Button>
            <ExportBar titulo={`Planejamento ${fmtData(ini)} a ${fmtData(fim)}`} colunas={cols} linhas={ops || []} testid="export-plan" />
          </div>
        </div>
        {!ops ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <div className="grid gap-3 md:grid-cols-7" data-testid="calendario-semana">
            {DIAS.map((d, i) => {
              const dia = addDays(ini, i);
              const lista = ops.filter((o) => o.data === dia);
              return (
                <Card key={dia} className={`bg-card/70 p-2 space-y-2 min-h-[160px] ${dia === today() ? "ring-1 ring-primary/60" : ""}`} data-testid={`dia-${dia}`}>
                  <div className="flex items-center justify-between">
                    <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{d}</div><div className="font-mono text-sm font-bold">{fmtData(dia).slice(0, 5)}</div></div>
                    <div className="flex">
                      {lista.length > 0 && <Button size="icon" variant="ghost" className="h-7 w-7" title="Requisição do dia" onClick={() => requisitar({ data: dia })} data-testid={`btn-req-dia-${dia}`}><ClipboardList className="w-3.5 h-3.5" /></Button>}
                      {tec && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => novo(dia)} data-testid={`btn-add-op-${dia}`}><Plus className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </div>
                  {lista.map((op) => <OpCard key={op.id} op={op} tec={tec} onEdit={(o) => setForm({ ...o })} onAction={action} />)}
                </Card>
              );
            })}
          </div>
        )}
      </FabricaGate>
      <OrdemDialog form={form} setForm={setForm} produtos={produtos} onSave={save} saving={saving} />
      <DiffDialog diff={diff} onClose={() => setDiff(null)} />
    </div>
  );
}
